import { Injectable } from '@angular/core';
import {
    BlockScrollStrategy,
    Overlay,
    OverlayRef,
    RepositionScrollStrategy,
    ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AssetDrillDownComponent } from '@app/shared/components/asset-drill-down/asset-drill-down.component';
import { ENDPOINTS } from '@core/constants/endpoints';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { ExploreDrillDownTimeRange } from '@app/shared/models/explore-drill-down.model';
import { switchMap, take } from 'rxjs/operators';
import { DrillDownOptions, LiveAssetStatus } from '@app/shared/models/live-status.model';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { AssetService } from '@core/services/asset.service';

interface OverlayConfig {
    assetId: number;
    scope: string;
    options: DrillDownOptions;
    overlayRef: OverlayRef;
}

interface DrillDownConfig {
    assetId: number;
}

@Injectable()
export class AssetDrillDownHelperService {
    private _drillDownConfig: DrillDownConfig;
    private _overlayConfig: OverlayConfig;
    private overlayRef: OverlayRef;
    private dataRefresh$: BehaviorSubject<void>;
    private drillDownData$: Observable<LiveAssetStatus>;

    get overlayConfig(): OverlayConfig {
        return this._overlayConfig;
    }

    get drillDownConfig(): DrillDownConfig {
        return this._drillDownConfig;
    }

    set drillDownConfig(config: DrillDownConfig) {
        this._drillDownConfig = config;
    }

    get data$(): Observable<LiveAssetStatus> {
        return this.drillDownData$;
    }

    constructor(
        private overlay: Overlay,
        private scrollStrategyOptions: ScrollStrategyOptions,
        private location: Location,
        private router: Router,
        private assetService: AssetService
    ) {
        this.dataRefresh$ = new BehaviorSubject<void>(undefined);
        this.drillDownData$ = this.dataRefresh$.pipe(
            switchMap(() => {
                if (!this._overlayConfig?.assetId) {
                    return EMPTY;
                }

                return this.assetService.getLiveStatus(this._overlayConfig.assetId);
            })
        );
    }

    showDrillDown(
        assetId: number,
        target: HTMLElement,
        scope: string,
        options: DrillDownOptions,
        closeCallbackFn?: () => void
    ) {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(target)
            .withLockedPosition()
            .withPositions([
                {
                    originX: 'start',
                    originY: options.elementPosition,
                    overlayX: 'start',
                    overlayY: 'top',
                },
            ]);

        const scrollStrategy: RepositionScrollStrategy | BlockScrollStrategy =
            this.scrollStrategyOptions.reposition();

        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-light-backdrop',
            positionStrategy,
            scrollStrategy,
        });

        const drillDownPortal = new ComponentPortal(AssetDrillDownComponent);
        const componentRef = this.overlayRef.attach(drillDownPortal);
        componentRef.instance.width = options.width ?? 300;
        componentRef.instance.error.pipe(take(1)).subscribe(() => {
            this.closeOverlay();
            closeCallbackFn();
        });

        this.overlayRef
            .backdropClick()
            .pipe(take(1))
            .subscribe(() => {
                this.closeOverlay();

                if (closeCallbackFn) {
                    closeCallbackFn();
                }
            });

        this._overlayConfig = {
            assetId,
            scope,
            options,
            overlayRef: this.overlayRef,
        };
        this.dataRefresh$.next();
    }

    updatePosition(target: HTMLElement) {
        this.dataRefresh$.next();

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(target)
            .withLockedPosition()
            .withPositions([
                {
                    originX: 'start',
                    originY: this._overlayConfig.options.elementPosition,
                    overlayX: 'start',
                    overlayY: 'top',
                },
            ]);

        this.overlayRef.updatePositionStrategy(positionStrategy);
    }

    closeDrillDown() {
        this._overlayConfig = undefined;
        this.closeOverlay();
    }

    updateExploreDrillDownUrl(
        assetId: number,
        dateRange?: ExploreDrillDownTimeRange,
        groups?: string[]
    ) {
        let url = ENDPOINTS.urls.dashboard.explore;
        let queryParams = '';

        if (assetId) {
            this._drillDownConfig = {
                assetId: assetId,
            };

            url = `${url}/${assetId}`;

            const params = { from: dateRange.from, to: dateRange.to };
            if (groups) {
                params['groups'] = groups;
            }

            queryParams = Object.entries(params)
                .reduce((prev, curr) => `${prev}${curr[0]}=${curr[1]}&`, '')
                .slice(0, -1);
        }

        this.location.replaceState(url, queryParams);
    }

    navigateToAssetExplore(
        assetId: number,
        dateRange: ExploreDrillDownTimeRange,
        exploreGroups: string[]
    ) {
        this._drillDownConfig = {
            assetId: assetId,
        };

        const url = `${ENDPOINTS.urls.dashboard.explore}/${assetId}`;
        const params = { from: dateRange.from, to: dateRange.to };

        if (exploreGroups.length) {
            params['groups'] = exploreGroups;
        }

        this.router.navigate([url], { queryParams: params }).catch(() => {});
    }

    private closeOverlay(): void {
        this.overlayRef.detach();
        this.overlayRef.dispose();
    }
}
